import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TutorialStep {
  id: string;
  title: string;
  content: string;
  element?: string; // CSS selector for the element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right';
}

type TutorialSteps = {
  [key: string]: TutorialStep[];
};

interface TutorialState {
  isActive: boolean;
  currentStepIndex: number;
  currentTutorial: string;
  hasCompletedTutorials: string[];
  tutorials: TutorialSteps;
  setIsActive: (active: boolean) => void;
  setCurrentTutorial: (tutorialName: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTutorial: () => void;
  resetTutorial: () => void;
  getCurrentSteps: () => TutorialStep[];
}

// Main navigation tutorial steps
const mainNavigationSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Divine Intelligence',
    content: 'Let\'s take a quick tour of the app\'s features. You can exit this tutorial at any time.',
    position: 'bottom'
  },
  {
    id: 'read',
    title: 'Bible Reader',
    content: 'Start your journey here with our Bible Reader, where you can explore different books and chapters.',
    element: '[data-tutorial="read"]',
    position: 'right'
  },
  {
    id: 'ai-insights',
    title: 'AI Insights',
    content: 'Get AI-powered insights and analysis of scripture passages.',
    element: '[data-tutorial="ai-insights"]',
    position: 'right'
  },
  {
    id: 'mood',
    title: 'Mood Scriptures',
    content: 'Find scriptures that resonate with your current emotional state.',
    element: '[data-tutorial="mood"]',
    position: 'right'
  },
  {
    id: 'study',
    title: 'Bible Study',
    content: 'Access study tools and commentary for deeper understanding.',
    element: '[data-tutorial="study"]',
    position: 'right'
  },
  {
    id: 'notes',
    title: 'Study Notes',
    content: 'Take and organize your study notes as you explore the scriptures.',
    element: '[data-tutorial="notes"]',
    position: 'right'
  },
  {
    id: 'search',
    title: 'Search',
    content: 'Search through the Bible to find specific passages or topics.',
    element: '[data-tutorial="search"]',
    position: 'right'
  }
];

// Bible Reader tutorial steps
const bibleReaderSteps: TutorialStep[] = [
  {
    id: 'welcome-reader',
    title: 'Bible Reader',
    content: 'Let\'s explore the features of the Bible Reader.',
    position: 'bottom'
  },
  {
    id: 'version-selector',
    title: 'Bible Version',
    content: 'You can choose from different Bible translations using the version selector at the top of the page. Here\'s an example:',
    position: 'bottom'
  },
  {
    id: 'navigation',
    title: 'Chapter Navigation',
    content: 'Navigate between books and chapters using these selectors at the top of the page. Here\'s how they look:',
    position: 'bottom'
  },
  {
    id: 'chapter-insights',
    title: 'AI Chapter Insights',
    content: 'Press this icon next to fullscreen to get AI-powered insights for the entire chapter.',
    position: 'bottom'
  },
  {
    id: 'verse-interaction',
    title: 'Verse Interaction',
    content: 'When you click on any verse, you\'ll see these options for Individual Verse Insights, Add Note, and Add to Favorites:',
    position: 'bottom'
  },
  {
    id: 'verse-insights',
    title: 'AI Verse Insights',
    content: 'Press this icon and select brief or detailed to get AI-powered insights specific to that verse.',
    position: 'bottom'
  }
];

// AI Insights tutorial steps
const aiInsightsSteps: TutorialStep[] = [
  {
    id: 'welcome-ai',
    title: 'Welcome to AI Insights',
    content: 'Let\'s explore how to use AI to gain deeper understanding of scripture. This powerful tool can help you study the Bible in new ways.',
    position: 'bottom'
  },
  {
    id: 'ai-input',
    title: 'Ask Your Questions',
    content: 'Type your Bible-related questions here. You can ask about:\n• Scripture interpretation\n• Historical context\n• Life application\n• Biblical concepts\n• Character studies',
    element: '[data-tutorial="ai-input"]',
    position: 'right'
  },
  {
    id: 'ai-history',
    title: 'View Previous Questions',
    content: 'Access your conversation history here. Your previous questions and their answers are saved for future reference. Click any question to ask it again.',
    element: '[data-tutorial="ai-history"]',
    position: 'top'
  },
  {
    id: 'verse-references',
    title: 'Interactive Bible References',
    content: 'When you receive an answer, any Bible references will be clickable links. Click them to read the full chapter in context.',
    element: '[data-tutorial="ai-response"]',
    position: 'bottom'
  }
];

// Mood Scriptures tutorial steps
const moodScripturesSteps: TutorialStep[] = [
  {
    id: 'welcome-mood',
    title: 'Welcome to Mood Scriptures',
    content: 'Find Bible verses that speak to your current emotional state or life situation. Let\'s explore how to use this feature.',
    position: 'bottom'
  },
  {
    id: 'search-situation',
    title: 'Describe Your Situation',
    content: 'Type your situation here. Examples:\n• "Feeling overwhelmed"\n• "Need relationship guidance"\n• "Looking for hope"',
    element: '[data-tutorial="custom-situation"]',
    position: 'bottom'
  },
  {
    id: 'mood-categories',
    title: 'Browse Categories',
    content: 'Find verses by category:\n• Emotional Struggles\n• Positive States\n• Life Situations\n• Spiritual Growth\n• Spiritual Challenges\n• Relationship Healing',
    element: '[data-tutorial="mood-categories"]',
    position: 'left'
  },
  {
    id: 'select-mood',
    title: 'Choose Your Mood',
    content: 'Click any mood that matches your feelings. Our AI will find relevant Bible verses for comfort and guidance.',
    element: '[data-tutorial="mood-selection"]',
    position: 'bottom'
  },
  {
    id: 'verse-results',
    title: 'Verse Actions',
    content: '• Tap verse to read full context\n• Use heart icon to save favorites\n• See why each verse was chosen',
    element: '[data-tutorial="verse-actions"]',
    position: 'bottom'
  }
];

// Search page tutorial steps
const searchTutorialSteps: TutorialStep[] = [
  {
    id: 'welcome-search',
    title: 'Bible Search',
    content: 'Welcome to Bible Search! Here you can search the entire Bible using keywords, phrases, or specific verse references.',
    position: 'bottom'
  },
  {
    id: 'search-tabs',
    title: 'Two Ways to Find Verses',
    content: '• Search Tab: Find verses using keywords or references\n• Biblical Themes Tab: Browse pre-organized collections of verses by theme',
    element: '[data-tutorial="search-tabs"]',
    position: 'bottom'
  },
  {
    id: 'search-input',
    title: 'Smart Search',
    content: 'Search using:\n• Keywords (e.g., "faith", "hope", "love")\n• Exact phrases (e.g., "be still and know")\n• Bible references (e.g., "John 3:16", "Psalm 23")',
    element: '[data-tutorial="search-input"]',
    position: 'bottom'
  },
  {
    id: 'search-filters',
    title: 'Advanced Search Options',
    content: '• Testament Filter: Focus on Old or New Testament\n• Sort Options: By relevance or book order\n• Results Size: Choose how many results to view at once',
    element: '[data-tutorial="search-filters"]',
    position: 'left'
  },
  {
    id: 'search-results',
    title: 'Understanding Results',
    content: 'Each search result shows:\n• Book, chapter, and verse reference\n• Full verse text\n• Click any verse to open it in the Bible reader',
    element: '[data-tutorial="search-results"]',
    position: 'bottom'
  }
];

// Define all tutorials here
const allTutorials: TutorialSteps = {
  'main-navigation': mainNavigationSteps,
  'bible-reader': bibleReaderSteps,
  'ai-insights': aiInsightsSteps,
  'mood-scriptures': moodScripturesSteps,
  'search': searchTutorialSteps,
  // Add more page tutorials here as we create them
};

export const useTutorialStore = create<TutorialState>()(
  persist(
    (set, get) => ({
      isActive: false,
      currentStepIndex: 0,
      currentTutorial: 'main-navigation',
      hasCompletedTutorials: [], // This will now always be empty
      tutorials: allTutorials,

      setIsActive: (active: boolean) => {
        if (!active) {
          set({ currentStepIndex: 0 });
        }
        set({ isActive: active });
      },

      setCurrentTutorial: (tutorialName: string) => {
        if (tutorialName in allTutorials) {
          set({ 
            currentTutorial: tutorialName,
            currentStepIndex: 0,
            isActive: true,
            tutorials: allTutorials
          });
        }
      },

      getCurrentSteps: () => {
        const { currentTutorial } = get();
        return allTutorials[currentTutorial] || [];
      },

      nextStep: () => {
        const { currentStepIndex, currentTutorial } = get();
        const steps = allTutorials[currentTutorial] || [];
        
        if (currentStepIndex < steps.length - 1) {
          set({ currentStepIndex: currentStepIndex + 1 });
        } else {
          // Tutorial completed - but don't store completion
          set({ 
            isActive: false,
            currentStepIndex: 0
          });
        }
      },

      previousStep: () => {
        const { currentStepIndex } = get();
        if (currentStepIndex > 0) {
          set({ currentStepIndex: currentStepIndex - 1 });
        }
      },

      skipTutorial: () => {
        set({ 
          isActive: false,
          currentStepIndex: 0
        });
      },

      resetTutorial: () => {
        set({ 
          currentStepIndex: 0,
          isActive: true,
          currentTutorial: 'main-navigation',
          hasCompletedTutorials: [], // Keep this empty
          tutorials: allTutorials
        });
      }
    }),
    {
      name: 'tutorial-storage-v3',
      partialize: (state) => ({
        // Only persist these states, not the hasCompletedTutorials
        currentTutorial: state.currentTutorial,
        currentStepIndex: state.currentStepIndex,
        isActive: state.isActive
      })
    }
  )
);
